// Home — a landing page, not a brochure. One statement, three doors, the
// proof, the address. Everything a cold visitor needs to pick a door is above
// the fold on a laptop and one short scroll on a phone; everything else lives
// behind the door they pick.

import React from 'react';
import { Bay, Btn, Eyebrow, Figures, Head, Shell } from '../primitives.jsx';
import { useSiteData } from '../data.js';
import { go } from '../routes.js';
import { STUDIO } from '../content.js';

function Door({ d }) {
  return (
    <a className="hddn-door" href={d.href} onClick={go(d.href)}>
      <span className="hddn-door__k">{d.k}</span>
      <span className="hddn-door__h">{d.h}</span>
      <span className="hddn-door__p">{d.p}</span>
      <span className="hddn-door__go">{d.go} &rarr;</span>
    </a>
  );
}

export default function Home() {
  const d = useSiteData();
  const { copy, caseStudy } = d;
  const doors = copy.doors || [];

  return (
    <>
      <section className="hddn-band--ground" style={{ paddingTop: 'calc(var(--bay) * 0.8)' }}>
        <Shell>
          <Eyebrow>{copy.heroEyebrow}</Eyebrow>
          <h1 className="hddn-h1" style={{ marginTop: 18, maxWidth: '15ch' }}>{copy.heroTitle}</h1>
          <p className="hddn-lede" style={{ marginTop: 22, maxWidth: '52ch' }}>{copy.heroLede}</p>
        </Shell>
        {/* The fork sits half over the dark band and half over the paper, so it
            reads as the seam between the parts of the business. */}
        <Shell>
          <div style={{ position: 'relative', top: 'clamp(24px, 5vw, 46px)', marginTop: 'clamp(28px, 5vw, 52px)' }}>
            <p className="hddn-label" style={{ color: 'var(--ground-muted)', marginBottom: 12 }}>{copy.forkNote}</p>
            <div className={`hddn-fork${doors.length === 3 ? ' hddn-fork--3' : ''}`}>
              {doors.map((door) => <Door key={door.id} d={door} />)}
            </div>
          </div>
        </Shell>
        <div style={{ height: 'clamp(24px, 5vw, 46px)' }} />
      </section>

      {/* The proof. Catchment share leads: a local client cares more that the
          audience genuinely lives here than that it is large. */}
      <Bay band="raised">
        <Head
          eyebrow={`${caseStudy.months} months · ${caseStudy.client}`}
          title="We can show you the numbers."
          lede={`Running the ${caseStudy.client} social accounts, ${caseStudy.window}. Not a case study written after the fact — this is the reporting.`}
        />
        {/* No onward link here on purpose: /content opens with these same four
            figures, so "see the full case study" landed on what you just read. */}
        <Figures items={caseStudy.headline} cols={4} />
      </Bay>

      <Bay band="ground">
        <div className="hddn-grid hddn-grid--sidebar">
          <div>
            <Eyebrow>Find us</Eyebrow>
            <p className="hddn-h3" style={{ marginTop: 14 }}>
              {STUDIO.street}<br />{STUDIO.city}, {STUDIO.state} {STUDIO.zip}
            </p>
            <p className="hddn-lede" style={{ marginTop: 16, maxWidth: '46ch' }}>
              Five to ten minutes' walk from Metro-North. Free street parking after 7 PM
              and all day Sunday. Look for the tunnel entrance.
            </p>
          </div>
          <div style={{ display: 'grid', gap: 12, alignContent: 'end' }}>
            <Btn href="/contact" onClick={go('/contact')} variant="fill" wide>Start a conversation</Btn>
            <Btn href="/book" onClick={go('/book')} variant="line" wide>Check availability</Btn>
          </div>
        </div>
      </Bay>
    </>
  );
}
